import { CircularProgress, makeStyles } from '@material-ui/core'
import type { SpecIntelligenceReport, SpecIntelligenceRunStatus } from 'api/graphQL/types/specIntelligence.types'
import type { SpecIntelligenceDrawerView } from 'components/SpecIntelligence/useSpecIntelligence'
import React, { useState } from 'react'
import SpecIntelligenceDrawerHeader from './SpecIntelligenceDrawerHeader'
import SpecIntelligenceErrorView from './SpecIntelligenceErrorView'
import SpecIntelligenceInfoDialog from './SpecIntelligenceInfoDialog'
import SpecIntelligencePickerView from './SpecIntelligencePickerView'
import SpecIntelligenceProgressView from './SpecIntelligenceProgressView'
import SpecIntelligenceResultsView from './SpecIntelligenceResultsView'

const useStyles = makeStyles(() => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        height: '100%',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
    },
    content: {
        flex: 1,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    loadingCenter: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
    },
}))

/** Props for {@link SpecIntelligenceDrawer}. */
export interface SpecIntelligenceDrawerProps {
    /** Called when the user clicks the close button or the panel is dismissed. */
    onClose: () => void
    /** Current view to display — owned by {@link useSpecIntelligence}. */
    drawerView: SpecIntelligenceDrawerView
    /** Latest run status from the parent hook; null when idle. */
    runStatus: SpecIntelligenceRunStatus | null
    /** Completed report for the results view; null when unavailable. */
    currentReport: SpecIntelligenceReport | null
    /**
     * Section numbers to pre-select in the picker when re-running — taken from
     * the previous report's analyzed sections.
     */
    rerunSectionNumbers?: string[] | null
    /** Start a new analysis run with the given section IDs. */
    onRunAnalysis: (selectedLeafIds: string[]) => void
    /** Reset back to the picker view from results view. */
    onRerun: () => void
}

/**
 * Pure-display right-pane drawer for Spec Intelligence. Renders one of four
 * views (loading / picker / progress / results) based on the `drawerView` prop.
 * All API calls live in {@link useSpecIntelligence} — this component has no
 * GraphQL dependencies.
 */
const SpecIntelligenceDrawer = ({
    onClose,
    drawerView,
    runStatus,
    currentReport,
    rerunSectionNumbers,
    onRunAnalysis,
    onRerun,
}: SpecIntelligenceDrawerProps) => {
    const classes = useStyles()

    const [infoOpen, setInfoOpen] = useState(false)

    return (
        <div className={classes.container} role="region" aria-label="Spec Intelligence">
            <SpecIntelligenceDrawerHeader
                onInfoOpen={() => setInfoOpen(true)}
                onClose={onClose}
                onRerun={drawerView === 'results' || drawerView === 'error' ? onRerun : undefined}
            />

            <div className={classes.content}>
                {drawerView === 'loading' && (
                    <div className={classes.loadingCenter}>
                        <CircularProgress size={32} aria-label="Loading" />
                    </div>
                )}

                {drawerView === 'picker' && (
                    <SpecIntelligencePickerView
                        onRunAnalysis={onRunAnalysis}
                        initialSectionNumbers={rerunSectionNumbers ?? undefined}
                    />
                )}

                {drawerView === 'progress' && (
                    <SpecIntelligenceProgressView status={runStatus} />
                )}

                {drawerView === 'results' && currentReport && (
                    <SpecIntelligenceResultsView report={currentReport} onRerun={onRerun} />
                )}

                {drawerView === 'error' && (
                    <SpecIntelligenceErrorView
                        errorMessage={currentReport?.errorMessage ?? runStatus?.errorMessage ?? null}
                        onRerun={onRerun}
                    />
                )}
            </div>

            <SpecIntelligenceInfoDialog
                open={infoOpen}
                onClose={() => setInfoOpen(false)}
            />
        </div>
    )
}

export default SpecIntelligenceDrawer
